import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { partnerWorkspaceApi } from "./partnerWorkspaceApi";
import "./partnerWorkspace.css";

// 2026-02 hardening: this page is a plain GET, safe for email link-scanners/preview-unfurlers
// to pre-fetch — it never consumes the token itself. Only the explicit "Confirm sign-in"
// button's POST (below) does. A used/expired/mismatched token — whether burned by a scanner
// or genuinely re-used — always lands back HERE, inline, with a partner-facing message and a
// "Request a new link" path. There is no code path from this page (or the backend it calls)
// that can ever reach Google's login — see kernel/auth.py's MagicLinkAccountConflict for the
// account-safety guard on the backend side of this same flow.
const ERROR_COPY = {
  missing_token: "That link looks incomplete. Request a new one below.",
  invalid_or_expired: "That link has expired or was already used — links are single-use and valid for 15 minutes. Request a new one below.",
  no_access: "We couldn't find an active NJM production partner for this link. Contact NJM if this seems wrong.",
  account_conflict: "This email is linked to an existing NJM staff account and can't be used for partner sign-in. Contact NJM if this seems wrong.",
};

export default function PartnerWorkspaceConfirmSignIn() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [state, setState] = useState(token ? "idle" : "missing"); // idle | confirming | error | missing
  const [errorCode, setErrorCode] = useState(token ? null : "missing_token");

  const confirm = async () => {
    setState("confirming");
    try {
      await partnerWorkspaceApi.verify(token);
      localStorage.setItem("njm_last_portal", "staff"); // 2026-09 security fix — clears any stale agency marker
      // Full navigation (not React Router) so AuthContext mounts fresh and picks up the
      // just-set session cookie via its own /auth/me check.
      window.location.href = "/partner-portal/home";
    } catch (err) {
      setErrorCode(err?.response?.data?.detail?.code || "invalid_or_expired");
      setState("error");
    }
  };

  return (
    <div className="pw-theme min-h-screen flex items-center justify-center px-6" data-testid="partner-workspace-confirm-page">
      <div className="w-full max-w-[420px] pw-enter">
        <div className="flex justify-center mb-10">
          <Logo theme="light" size="lg" />
        </div>
        <div className="pw-card p-8 text-center">
          <span className="pw-eyebrow">Partner Workspace</span>

          {state === "error" ? (
            <div className="pw-enter mt-4" data-testid="partner-workspace-confirm-error">
              <h1 className="pw-display text-[24px] mb-4">Can't sign you in</h1>
              <p className="text-[14.5px] mb-6" style={{ color: "var(--pw-text-secondary)" }}>{ERROR_COPY[errorCode] || ERROR_COPY.invalid_or_expired}</p>
              <Link to="/partner-portal/login" data-testid="partner-workspace-confirm-request-new-link" className="pw-btn-primary inline-block px-5 py-2.5 text-sm">Request a new link</Link>
            </div>
          ) : (
            <div className="mt-4">
              <h1 className="pw-display text-[24px] mb-4">Confirm sign-in</h1>
              <p className="text-[14.5px] mb-7" style={{ color: "var(--pw-text-secondary)" }}>
                Click below to finish signing in to your NJM Partner Workspace. This link is valid once, for 15 minutes.
              </p>
              <button onClick={confirm} disabled={state === "confirming" || state === "missing"} data-testid="partner-workspace-confirm-signin-btn" className="pw-btn-primary w-full py-3 text-sm">
                {state === "confirming" ? "Signing you in…" : "Confirm sign-in"}
              </button>
              {state === "missing" && (
                <p className="text-xs mt-4" style={{ color: "var(--pw-text-faint)" }}>
                  That link looks incomplete. <Link to="/partner-portal/login" className="font-bold underline">Request a new one</Link>.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
